import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { FilterDTO } from '@tempo-io/roadmap-dto';
import { ItemsApi } from '@/lib/api/common/items';
import type { ItemFiltersType } from '@/lib/api/types/itemFilters';
import type { Item, ItemRequestModelField } from '@/lib/api/types/items';
import { API_MAX_PAGE_LIMIT } from '@/lib/constants';
import { commonQueryKeys } from '@/lib/query-keys/common';
import { viewQueryKeys } from '@/lib/query-keys/view';

export const useItemsBulkFetchQuery = (
  {
    itemIds,
    fields,
    filter,
    viewId,
  }: {
    itemIds: number[];
    fields?: ItemRequestModelField[];
    filter?: ItemFiltersType;
    viewId?: number;
  },
  staleTime?: number,
  enabled = true,
) => {
  const queryClient = useQueryClient();

  const cachedFilters = viewId
    ? (queryClient.getQueryData<FilterDTO[]>(viewQueryKeys.filter.list(viewId)) ?? [])
    : [];

  const filtersLoaded = !filter || cachedFilters.length > 0;
  return useQuery<Item[]>({
    queryKey: [
      ...commonQueryKeys.itemsBulkFetch.byParams({ ids: itemIds, fields, filter }),
      ...(viewId ? [viewQueryKeys.filter.list(viewId)] : []),
    ],
    queryFn: async () => {
      // The API only allows MAX_PAGE_LIMIT items at a time.
      const chunks = itemIds.reduce((acc: number[][], _, index) => {
        if (index % API_MAX_PAGE_LIMIT === 0) {
          acc.push(itemIds.slice(index, index + API_MAX_PAGE_LIMIT));
        }
        return acc;
      }, []);
      const responses = await Promise.all(
        chunks.map((chunk) =>
          ItemsApi.getItemsBulkFetch({
            ids: chunk,
            fields,
            filter: filter,
          }),
        ),
      );

      return responses.flat();
    },
    enabled: enabled && filtersLoaded,
    ...(staleTime !== undefined && { staleTime }),
    refetchOnMount: false,
    placeholderData: (previousData) => previousData,
  });
};
